import React from 'react'
import moment from 'moment'

import './styles.css'
import { Message } from 'semantic-ui-react'
import { withTranslation } from 'react-i18next'
import { formatDateForApiCall } from '../../../../utils'
import { getSubsidiaryIdsForRegion } from '../utils/kegCollectionFormUtils'
import createAction from '../../../../redux/actions/createAction'

import KegCollectionBookingsTableFiltersForm from './KegCollectionBookingsTableFiltersForm/KegCollectionBookingsTableFiltersForm'
import KegCollectionBookingsTable from './KegCollectionBookingsTable/KegCollectionBookingsTable'
import KegCollectionCreateModal from './KegCollectionCreateModal/KegCollectionCreateModal'
import MultiOptionToggleBtnGroup from '../../../../common-components/MultiOptionToggleBtnGroup'
import BookingsGeneralNotificationMessage from './shared/BookingsGeneralNotificationMessage'
import SkeletonTable from '../../../../common-components/SkeletonTable/SkeletonTable'
import UiContainer from '../../../../common-components/UiContainer'

import {
  FETCH_KEG_COLLECTION_BOOKINGS,
  FETCH_KEG_COLLECTION_BOOKING_COLLECTORS,
  SAVE_SINGLE_KEG_COLLECTION_BOOKING,
  FETCH_SINGLE_KEG_COLLECTION_BOOKING_SUCCESS,
  HIDE_BOOKINGS_GENERAL_NOTIFICATION_MESSAGE,
  RESET_CURRENT_USER_STATE,
  SHOW_BOOKINGS_GENERAL_NOTIFICATION_MESSAGE,
  RESET_CREATE_KEG_COLLECTION_STATUS_STATE,
  FETCH_KEG_COLLECTION_BOOKINGS_LIST_VIEW_SEARCH_FILTER_OPTIONS,
} from '../../../../redux/actions/actionTypes'
import { DEFAULT_DISPLAYED_DATE_FORMAT } from '../../../../constants/formAndApiUrlConfig/commonConfig'
import {
  BOOKINGS_CUSTOMER_NAME,
  BOOKINGS_END_DATE,
  BOOKINGS_POSTAL_CODE,
  BOOKINGS_START_DATE,
  BOOKINGS_STATUS,
  RESET_FILTERS,
  PAGE_SIZE,
  PAGE_NUMBER,
  REGION_CHANGE,
  BOOKINGS_ASSIGNED_COLLECTOR,
  BOOKINGS_PREFERRED_COLLECTOR,
  BOOKINGS_CREATED_BY,
  BOOKINGS_VENUE_STATE,
  BOOKINGS_ASSIGNED_BY,
  REFERENCE_ID,
} from '../utils/actionTypes'
import useSessionStorageState from '../../../../common-components/hooks/useSessionStorage'
import useLocalStorageState from '../../../../common-components/hooks/useLocalStorage'

const initialDateValues = {
  startDateString: moment().subtract(60, 'days').format(DEFAULT_DISPLAYED_DATE_FORMAT),
  endDateString: moment().endOf('day').format(DEFAULT_DISPLAYED_DATE_FORMAT),
}

const bookingsAPIParamsInitialState = {
  // Available but not implemented in the UI:
  // startEstimatedPickupDate: '',
  // endEstimatedPickupDate: '',
  // customerId: '',
  // customerTapId: '',
  // assignmentStatus: Possible values: ASSIGNED | UNASSIGNED '',
  createdBy: '',
  assignedBy: '',
  preferredCollectorId: '',
  stateOrProvince: '',
  collectorCustomerId: '',
  region: 'UKE',
  pageSize: 10,
  pageNumber: 1,
  customerName: '',
  postalCode: '',
  statusId: 'PENDING_ASSIGNED_SCHEDULED',
  startCreatedDate: formatDateForApiCall({
    date: initialDateValues.startDateString,
    startOfDayRatherThanNoonLocalTime: true,
  }),
  endCreatedDate: formatDateForApiCall({
    date: initialDateValues.endDateString,
    endOfDayRatherThanNoonLocalTime: true,
  }),
  bookingReferenceId: '',
}

const apiValuesReducer = (state, action) => {
  switch (action.type) {
    case REFERENCE_ID:
      return { ...state, bookingReferenceId: action.value, pageNumber: 1 }
    case BOOKINGS_CUSTOMER_NAME:
      return { ...state, customerName: action.value, pageNumber: 1 }
    case BOOKINGS_POSTAL_CODE:
      return { ...state, postalCode: action.value, pageNumber: 1 }
    case BOOKINGS_STATUS:
      return { ...state, statusId: action.value, pageNumber: 1 }
    case BOOKINGS_START_DATE:
      return {
        ...state,
        startCreatedDate: action.value,
        pageNumber: 1,
      }
    case BOOKINGS_END_DATE:
      return {
        ...state,
        endCreatedDate: action.value,
        pageNumber: 1,
      }
    case PAGE_SIZE:
      return {
        ...state,
        pageSize: action.value,
        pageNumber: 1,
      }
    case PAGE_NUMBER:
      return {
        ...state,
        pageNumber: action.value,
      }
    case REGION_CHANGE:
      // Reset only postalCode, assignedCollector, preferredCollector
      // state and referenceId because these values are unique to each region
      // and should not persist across regions.
      // Persist only: venue name, status, assigned status, date requested from and to,
      // created by, and assigned by.
      return {
        ...state,
        postalCode: '',
        collectorCustomerId: '',
        preferredCollectorId: '',
        stateOrProvince: '',
        bookingReferenceId: '',
        region: action.value,
      }
    case BOOKINGS_ASSIGNED_COLLECTOR:
      return {
        ...state,
        collectorCustomerId: action.value,
        pageNumber: 1,
      }
    case BOOKINGS_PREFERRED_COLLECTOR:
      return {
        ...state,
        preferredCollectorId: action.value,
        pageNumber: 1,
      }
    case BOOKINGS_CREATED_BY:
      return { ...state, createdBy: action.value, pageNumber: 1 }
    case BOOKINGS_VENUE_STATE:
      return { ...state, stateOrProvince: action.value, pageNumber: 1 }
    case BOOKINGS_ASSIGNED_BY:
      return { ...state, assignedBy: action.value, pageNumber: 1 }
    case RESET_FILTERS:
      return { ...bookingsAPIParamsInitialState, pageSize: state.pageSize }
    default:
      return state
  }
}

const SESSION_KEYS = {
  apiParams: 'kegCollectionBookings.table.bookingsAPIParams',
  pageSize: 'kegCollectionBookings.table.pageSize',
  regionFilter: 'kegCollectionBookings.table.regionFilter',
  extraFieldsEnabled: 'kegCollectionBookings.table.filters.extraFieldsEnabled',
  fieldValues: 'kegCollectionBookings.table.filters.fieldValues',
}

export default withTranslation()(
  UiContainer({
    headerText: 'Keg Collection Bookings',
    containerId: 'kegCollectionBookingsUserSection',
  })(props => {
    const {
      t: translate,
      bookingsList,
      pageNumber,
      pageSize,
      totalElements,
      totalPages,
      isFetching,
      didFetchSucceed,
      didFetchFail,
      dispatch,
      i18n,
      noResultsMessage,
      history,
      venuesList,
      selectedVenueCollectionInfo,
      currentUserId,
      createKegCollectionRequestStatusState,
      deleteKegCollectionStatusState,
      currentUserOperateAs,
      collectorsList,
      bookingsExportList,
      bookingsListViewSearchFilterOptions,
    } = props

    React.useEffect(() => {
      // Reset current user if current user data exists in Redux store
      // This is needed to prevent network errors
      if (currentUserOperateAs?.id) {
        dispatch(createAction(RESET_CURRENT_USER_STATE))
      }
    }, [currentUserOperateAs, dispatch])

    const [storedRegionFilter, setStoredRegionFilter] = useLocalStorageState({
      key: SESSION_KEYS.regionFilter,
      initialValue: 'UKE',
      version: '1',
    })

    const [storedPageSize, setStoredPageSize] = useLocalStorageState({
      key: SESSION_KEYS.pageSize,
      initialValue: bookingsAPIParamsInitialState.pageSize,
      version: '1',
    })

    const [extraFieldsEnabled, setExtraFieldsEnabled] = useLocalStorageState({
      key: SESSION_KEYS.extraFieldsEnabled,
      initialValue: [],
      version: '1',
    })

    const [storedApiParams, setStoredApiParams] = useSessionStorageState({
      key: SESSION_KEYS.apiParams,
      initialValue: {
        ...bookingsAPIParamsInitialState,
        pageSize: storedPageSize,
        region: storedRegionFilter,
      },
      version: '1',
    })

    const fieldValuesInitialState = React.useMemo(
      () => ({
        customerName: '',
        postalCode: '',
        status: ['PENDING', 'ASSIGNED', 'SCHEDULED'],
        assignedCollector: '',
        preferredCollector: '',
        createdBy: '',
        assignedBy: '',
        state: '',
        datePickerValues: {
          startDateString: initialDateValues.startDateString,
          endDateString: initialDateValues.endDateString,
        },
        referenceId: '',
      }),
      []
    )

    const [fieldValues, setFieldValues, resetFieldValues] = useSessionStorageState({
      key: SESSION_KEYS.fieldValues,
      initialValue: fieldValuesInitialState,
      version: '1',
    })

    const [bookingsAPIParams, dispatchBookingsAPIParams] = React.useReducer(apiValuesReducer, {
      ...storedApiParams,
      pageSize: storedPageSize,
    })

    const [showCreateModal, setShowCreateModal] = React.useState(false)

    const dispatchFetchBookingsAction = React.useCallback(
      (forceNetworkRequest = false) => {
        dispatch(
          createAction(FETCH_KEG_COLLECTION_BOOKINGS, {
            bookingsAPIParams,
            forceNetworkRequest,
          })
        )
      },
      [bookingsAPIParams, dispatch]
    )

    const fetchAndSetCollectorsList = React.useCallback(
      region => {
        dispatch(
          createAction(FETCH_KEG_COLLECTION_BOOKING_COLLECTORS, {
            customerParams: {
              ct: 'COL',
              subsidiaryIds: getSubsidiaryIdsForRegion(region),
              sts: 'ACT',
              includeDSHP: true,
            },
          })
        )
      },
      [dispatch]
    )

    const fetchAndSetSearchFilterOptions = React.useCallback(
      region => {
        dispatch(
          createAction(FETCH_KEG_COLLECTION_BOOKINGS_LIST_VIEW_SEARCH_FILTER_OPTIONS, {
            optionsParams: {
              subsidiaryIds: getSubsidiaryIdsForRegion(region),
            },
          })
        )
      },
      [dispatch]
    )

    const handleForceRefresh = React.useCallback(() => {
      dispatchFetchBookingsAction(true)
    }, [dispatchFetchBookingsAction])

    React.useEffect(() => {
      // Fetch and set collectors list and search filter options once, on initial render
      // Afterwards, collectors list and search filter options can update when region changes
      fetchAndSetCollectorsList(storedRegionFilter)
      fetchAndSetSearchFilterOptions(storedRegionFilter)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    React.useEffect(() => {
      if (createKegCollectionRequestStatusState?.didCreateSucceed) {
        dispatch(
          createAction(SHOW_BOOKINGS_GENERAL_NOTIFICATION_MESSAGE, {
            header: translate('Success'),
            content: translate('kegCollectionCreatedSuccessMessage'),
            isError: false,
          })
        )
        dispatch(createAction(RESET_CREATE_KEG_COLLECTION_STATUS_STATE))
        handleForceRefresh()
      }
    }, [
      createKegCollectionRequestStatusState?.didCreateSucceed,
      dispatch,
      translate,
      handleForceRefresh,
    ])

    React.useEffect(
      () => () => dispatch(createAction(HIDE_BOOKINGS_GENERAL_NOTIFICATION_MESSAGE)),
      [dispatch]
    )

    React.useEffect(() => {
      dispatchFetchBookingsAction(false)
      if (storedPageSize !== bookingsAPIParams.pageSize) {
        setStoredPageSize(bookingsAPIParams.pageSize)
      }
      if (storedRegionFilter !== bookingsAPIParams.region) {
        setStoredRegionFilter(bookingsAPIParams.region)
        fetchAndSetCollectorsList(bookingsAPIParams.region)
      }
      setStoredApiParams({ ...bookingsAPIParams, pageSize: storedPageSize })
    }, [
      bookingsAPIParams,
      dispatchFetchBookingsAction,
      setStoredApiParams,
      storedPageSize,
      setStoredPageSize,
      storedRegionFilter,
      setStoredRegionFilter,
      fetchAndSetCollectorsList,
    ])

    const handleOnRegionChange = React.useCallback(
      region => {
        // Reset only postalCode, assignedCollector, preferredCollector
        // state and referenceId because these values are unique to each region
        // and should not persist across regions.
        // Persist only: venue name, status, assigned status, date requested from and to,
        // created by, and assigned by.
        setFieldValues(prev => ({
          ...prev,
          postalCode: '',
          assignedCollector: '',
          preferredCollector: '',
          state: '',
          referenceId: '',
        }))
        dispatchBookingsAPIParams({ type: REGION_CHANGE, value: region })
        setStoredRegionFilter(region)
        fetchAndSetCollectorsList(region)
        fetchAndSetSearchFilterOptions(region)
      },
      [
        fetchAndSetCollectorsList,
        setStoredRegionFilter,
        fetchAndSetSearchFilterOptions,
        setFieldValues,
      ]
    )

    const isExtraFilterEnabled = React.useCallback(
      id => extraFieldsEnabled.includes(id),
      [extraFieldsEnabled]
    )

    let contentToRender = <></>
    if (isFetching) {
      contentToRender = (
        <SkeletonTable
          rows={10}
          columns={extraFieldsEnabled?.length > 0 ? extraFieldsEnabled.length + 8 : 8}
        />
      )
    } else if (didFetchSucceed) {
      contentToRender = (
        <KegCollectionBookingsTable
          translate={translate}
          bookingsList={bookingsList}
          i18n={i18n}
          noResultsMessage={noResultsMessage}
          history={history}
          dispatch={dispatch}
          createAction={createAction}
          saveSingleBookingActionType={SAVE_SINGLE_KEG_COLLECTION_BOOKING}
          fetchSingleBookingSuccessActionType={FETCH_SINGLE_KEG_COLLECTION_BOOKING_SUCCESS}
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalElements={totalElements}
          totalPages={totalPages}
          dispatchBookingsAPIParams={dispatchBookingsAPIParams}
          deleteKegCollectionStatusState={deleteKegCollectionStatusState}
          extraFieldsEnabled={extraFieldsEnabled}
          isExtraFilterEnabled={isExtraFilterEnabled}
          collectorsList={collectorsList}
        />
      )
    } else if (didFetchFail) {
      contentToRender = (
        <Message
          negative
          icon="exclamation triangle"
          header={translate('Error')}
          content={translate('errorFetchingBookings')}
        />
      )
    }

    return (
      <div className="bk_lv_listView animate-into-view-1">
        <BookingsGeneralNotificationMessage />
        <div id="kegCollectionBookingsActionsBar">
          <KegCollectionCreateModal
            translate={translate}
            toggleModal={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onOpen={() => {
              setShowCreateModal(true)
              setTimeout(() => {
                document.querySelector('#venueDropdown input.search')?.focus()
              }, 350)
            }}
            dispatch={dispatch}
            createAction={createAction}
            venuesList={venuesList}
            selectedVenueCollectionInfo={selectedVenueCollectionInfo}
            currentUserId={currentUserId}
            createKegCollectionRequestStatusState={createKegCollectionRequestStatusState}
            getSubsidiaryIdsForRegion={getSubsidiaryIdsForRegion}
            region={storedApiParams.region}
          />
          <div className="bk_lv_regionToggle">
            <MultiOptionToggleBtnGroup
              options={[
                {
                  key: 'bookings_toggle_btn_uke',
                  label: 'UKE',
                  titleText: translate('United Kingdom & Europe'),
                },
                {
                  key: 'bookings_toggle_btn_aus',
                  label: 'AUS',
                  titleText: translate('Australia'),
                },
                {
                  key: 'bookings_toggle_btn_nz',
                  label: 'NZ',
                  titleText: translate('New Zealand'),
                },
              ]}
              defaultValue={storedApiParams.region}
              onChange={label => handleOnRegionChange(label)}
              disabled={isFetching}
            />
          </div>
        </div>
        <div className="bk_lv_filtersSection">
          <KegCollectionBookingsTableFiltersForm
            translate={translate}
            dispatchBookingsAPIParams={dispatchBookingsAPIParams}
            formatDateForApiCall={formatDateForApiCall}
            initialDateValues={initialDateValues}
            defaultDisplayedDateFormat={DEFAULT_DISPLAYED_DATE_FORMAT}
            onForceRefresh={handleForceRefresh}
            isFetching={isFetching}
            collectorsList={collectorsList}
            extraFieldsEnabled={extraFieldsEnabled}
            setExtraFieldsEnabled={setExtraFieldsEnabled}
            isExtraFilterEnabled={isExtraFilterEnabled}
            fieldValues={fieldValues}
            setFieldValues={setFieldValues}
            resetFieldValues={resetFieldValues}
            fieldValuesInitialState={fieldValuesInitialState}
            bookingsList={bookingsList}
            i18n={i18n}
            bookingsExportList={bookingsExportList}
            dispatch={dispatch}
            bookingsAPIParams={bookingsAPIParams}
            bookingsListViewSearchFilterOptions={bookingsListViewSearchFilterOptions}
          />
        </div>
        <div className="bk_lv_contentSection">{contentToRender}</div>
      </div>
    )
  })
)
